/* product-addons.js — keeps the buy box's add-on checkboxes on the finish the
 * shopper picked for the product itself.
 *
 * Luke, 2026-09-18: "please make sure it's clear to the customer with the
 * addon for drain cover, etc. that it comes in the same colour when ordered
 * together, and please make sure that in our cart it's added as the same
 * colour variant picked for the main product. So that when the order goes
 * through to the supplier after we pass it through from Shopify to Aden that
 * it will correctly order the right variant." Then: "when the variant swatch
 * is picked for the main product, it's child addons show the variant swatch
 * selected in the addon also so it's crystal clear to the customer."
 *
 * The matching RULE lives here and only here. snippets/product-addons.liquid
 * applies the same rule server-side for the first frame only (the variant the
 * page was rendered with), so nothing flashes and nothing has to be corrected
 * a frame after paint — the Liquid is a pre-match, not a second implementation
 * that could drift.
 *
 * Rule: exact equality of the option VALUE, on the option whose NAME matches
 * the parent's key option (`Finish`, or the parent's first option when it has
 * no option so named). Never the SKU — PB8001-LIQS (bath) and PB001-40-LIQS
 * (drain cover) carry the same code in different shapes.
 *
 * Loaded (deferred) by the snippet itself, so a PDP with no add-ons never
 * fetches it. Listens on document for the `variant:change` assets/ui.js
 * bubbles off the product form; it holds no state of its own beyond the
 * parsed variant lists.
 */
(() => {
  'use strict';
  const box = document.querySelector('[data-addons]');
  if (!box) return;
  const keyName = box.dataset.addonKey || '';
  const keyIndex = parseInt(box.dataset.addonKeyIndex || '0', 10) || 0;
  if (!keyName) return; // product has no options: nothing ever changes

  const money = (c) => {
    try {
      return (c / 100).toLocaleString('en-AU', {
        style: 'currency',
        currency: (window.Shopify && window.Shopify.currency && window.Shopify.currency.active) || 'AUD',
      });
    } catch { return '$' + (c / 100).toFixed(2); }
  };

  const rows = [...box.querySelectorAll('[data-addon-item]')].map((el) => {
    const idx = parseInt(el.dataset.addonOptionIndex, 10);
    let variants = [];
    const json = el.querySelector('[data-addon-variants]');
    if (json) { try { variants = JSON.parse(json.textContent) || []; } catch {} }
    return {
      el,
      idx: Number.isFinite(idx) ? idx : -1,
      variants,
      input: el.querySelector('[data-addon]'),
      match: el.querySelector('[data-addon-match]'),
      text: el.querySelector('[data-addon-match-text]'),
      chip: el.querySelector('[data-addon-chip]'),
      elsewhere: el.querySelector('[data-addon-elsewhere]'),
      price: el.querySelector('[data-addon-price]'),
    };
    // rows with idx < 0 are the legacy single-variant add-ons (Turner
    // Hastings' colour sink waste): left exactly as the section rendered them.
  }).filter((r) => r.idx >= 0 && r.input && r.variants.length);
  if (!rows.length) return;

  /* The mini disc is the parent's OWN selected chip, copied. Re-deriving it
     here would mean a second copy of the brand swatch set in JS and a second
     chance to disagree with the picker two rows above it. */
  const parentChip = () => {
    // data-option-index on the fieldset is Shopify's 1-based option position
    const fs = document.querySelector('[data-product-form] .product__option[data-option-index="' + (keyIndex + 1) + '"]');
    return fs ? fs.querySelector('[data-option-value][aria-checked="true"]') : null;
  };

  const paintChip = (chipWrap, src) => {
    if (!chipWrap || chipWrap.hidden) return;
    const disc = chipWrap.firstElementChild;
    if (!disc || !src) return;
    const fill = src.style.getPropertyValue('--sw');
    if (fill) disc.style.setProperty('--sw', fill); else disc.style.removeProperty('--sw');
    const srcImg = src.querySelector('img');
    let img = disc.querySelector('img');
    if (srcImg) {
      if (!img) {
        // appended, never replacing the disc's contents: swatch.liquid puts a
        // .visually-hidden copy of the value in there for screen readers.
        img = document.createElement('img');
        img.width = 96; img.height = 96; img.alt = ''; img.loading = 'lazy'; img.decoding = 'async';
        disc.appendChild(img);
      }
      if (img.getAttribute('src') !== srcImg.getAttribute('src')) img.setAttribute('src', srcImg.getAttribute('src'));
      disc.classList.add('swatch--img');
    } else if (img) {
      img.remove();
      disc.classList.remove('swatch--img');
    }
  };

  const apply = (value) => {
    if (!value) return;
    const chipSrc = parentChip();
    rows.forEach((r) => {
      const v = r.variants.find((x) => x.v === value) || null;
      const ok = !!(v && v.a);
      r.input.disabled = !ok;
      if (!ok) r.input.checked = false;
      r.el.classList.toggle('is-unavailable', !ok);
      if (v) {
        // the id posted to /cart/add.js — this is the whole contract with the
        // order and with Aden, so it moves before anything cosmetic does
        r.input.value = String(v.id);
        r.input.dataset.price = String(v.p);
        r.input.dataset.sku = v.s || '';
        r.input.dataset.variantTitle = v.t || '';
      }
      if (r.price) {
        r.price.hidden = !ok;
        if (ok) r.price.textContent = '+ ' + money(v.p);
      }
      if (r.text) {
        r.text.textContent = !v
          ? 'Not available in ' + value + ' —'
          : (ok ? 'Matched to your ' + keyName.toLowerCase() + ' — ' + value : value + ' is sold out —');
      }
      if (r.match) r.match.classList.toggle('is-off', !ok);
      if (r.elsewhere) r.elsewhere.hidden = ok;
      if (r.chip) {
        r.chip.hidden = !chipSrc;
        paintChip(r.chip, chipSrc);
      }
    });
  };

  document.addEventListener('variant:change', (e) => {
    const v = e.detail;
    if (!v || !Array.isArray(v.options)) return;
    apply(v.options[keyIndex]);
  });
})();
